import Stack from '@mui/material/Stack';
import { useTranslation } from 'react-i18next';
import { useForm, FormProvider } from 'react-hook-form';
import { useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import LoadingButton from '@mui/lab/LoadingButton';
import { hashPassword } from '@mgm/common/utils';
import { PasswordValidation } from '@mgm/common/components/password';
import { GenericHeader } from '@mgm/common/components/base';
import { WelcomeWrapper } from '@mgm/common/wrappers';
import { useQueryParams } from '../../../hooks/useCommon';
import { LanguageSelect } from '../../../components';
import {
  PasswordField,
  TCustomFieldOptions,
  TFormInputs,
} from '../../../components/form';

import { useAppSelector } from '../../../hooks/useAppStore';
import { getPasswordValidation } from '../../../store/common/selectors';
import { usePasswordValidation, useValidation } from '../../../hooks/useValidation';
import { useRegisterAPI } from '../../../hooks/api/useRegister';

const Register = () => {
  const { t } = useTranslation(['welcome', 'inputs']);
  const query = useQueryParams();

  const { mutate: registerMutate, isLoading } = useRegisterAPI();

  const passwordValid = useAppSelector(getPasswordValidation);

  const {
    isEightCharacter,
    isSpecialCharacter,
    isLowercaseChar,
    isMaxCharacter,
    isUppercaseChar,
    isPasswordVerify,
  } = useValidation();

  const { validationItems } = usePasswordValidation(true);

  const [password, setPassword] = useState('');

  const methods = useForm<Pick<TFormInputs, TCustomFieldOptions>>({
    mode: 'all',
  });

  const isFormValid = useMemo(() => {
    const isPasswordValid = Object.values(passwordValid).every(
      (value) => value || value === null,
    );

    return methods.formState.isValid && isPasswordValid;
  }, [methods.formState.isValid, passwordValid]);

  const handleRegister = () => {
    const data = {
      password: hashPassword(password),
      inviteKey: query.get('inviteKey') || '',
    };

    registerMutate(data);
  };

  return (
    <WelcomeWrapper>
      <LanguageSelect />
      <GenericHeader
        subtitleProps={{
          children: t('welcomeToManageMate'),
          fontSize: 14,
        }}
        titleProps={{
          children: t('createAnAccount'),
        }}
      />
      <form
        noValidate
        onSubmit={methods.handleSubmit(handleRegister)}
      >
        <Stack spacing={3}>
          <FormProvider {...methods}>
            <PasswordField
              onChange={setPassword}
              isCheckValidation
              rules={{
                isEightCharacter,
                isSpecialCharacter,
                isLowercaseChar,
                isUppercaseChar,
                isMaxCharacter,
              }}
            />
            <PasswordField
              labelText={t('confirmPassword', { ns: 'inputs' })}
              placeholder={t('confirmNewPassword', { ns: 'inputs' })}
              name="confirmPassword"
              isCheckValidation
              rules={{
                isPasswordVerify: (val: string) => isPasswordVerify(password, val),
              }}
            />
            <PasswordValidation validationItems={validationItems} />
          </FormProvider>
        </Stack>
        <Box paddingY={2}>
          <LoadingButton
            fullWidth
            type="submit"
            variant="contained"
            loading={isLoading}
            disabled={!isFormValid || isLoading}
            sx={{ my: 2 }}
          >
            {t('createAccount', { ns: 'welcome' })}
          </LoadingButton>
        </Box>
      </form>
    </WelcomeWrapper>
  );
};

export {
  Register,
};
